/**
 * @const grecaptcha
 * @type {{execute: function}}
 */

import { inertElements } from "../common/helpers";

const TABS = ["/ajax/search-website", "/ajax/search-blog"];

export const useSearchDropdown = () => {
    const $body = document.querySelector(".js-ns-body");
    const $mobileHeader = document.querySelector(".js-ns-mobile-header");
    const $dropdown = document.querySelector(".js-ns-search-dropdown");
    const openElements = document.querySelectorAll(".js-ns-open-search");
    const $close = $dropdown.querySelector(".js-ns-search-close");
    const $form = $dropdown.querySelector(".js-ns-search-dropdown-form");
    const $input = $dropdown.querySelector(".js-ns-search-dropdown-input");
    const $reset = $dropdown.querySelector(".js-ns-search-dropdown-reset");
    const tabButtons = $dropdown.querySelectorAll(
        ".js-ns-search-dropdown-tab-button"
    );
    const counters = $dropdown.querySelectorAll(
        ".js-ns-search-dropdown-counter"
    );
    const tabs = $dropdown.querySelectorAll(".js-ns-search-dropdown-tab");
    const $searchInProgress = $dropdown.querySelector(
        ".js-ns-search-dropdown-in-progress"
    );
    const $itemTemplate = $dropdown.querySelector(
        "#ns-search-dropdown-item-template"
    );
    const $noResultTemplate = $dropdown.querySelector(
        "#ns-search-dropdown-no-result"
    );
    const $errorRecaptchaTemplate = $dropdown.querySelector(
        "#ns-search-dropdown-error-recaptcha"
    );
    const $showMoreTemplate = $dropdown.querySelector(
        "#ns-search-dropdown-more"
    );
    const tabsConfig = TABS.map((url) => ({
        url,
        page: 0,
        total: 0,
        items: [],
        isFetched: false,
    }));

    let $lastOpenElementClicked = null;
    let lastQuery = "";

    const handleInertElements = (enable = true) => {
        const elements = document.querySelectorAll(".ns-main, .ns-modal");

        inertElements(elements, enable);
    };

    const getNoResultElement = () => {
        const $clone = $noResultTemplate.content.cloneNode(true);

        return $clone;
    };

    const getErrorRecaptchaElement = () =>
        $errorRecaptchaTemplate.content.cloneNode(true);

    const getMoreButtonElement = () => {
        const $clone = $showMoreTemplate.content.cloneNode(true);

        return $clone;
    };

    const getItemElement = ({ title, path, content }) => {
        const $clone = $itemTemplate.content.cloneNode(true);
        const $item = $clone.querySelector(".js-ns-search-dropdown-item");
        const $title = $clone.querySelector(
            ".js-ns-search-dropdown-item-title"
        );
        const $path = $clone.querySelector(".js-ns-search-dropdown-item-path");
        const $content = $clone.querySelector(
            ".js-ns-search-dropdown-item-content"
        );

        $item.href = path;
        $title.innerHTML = title;
        $path.textContent = path;
        $content.innerHTML = content;

        return $clone;
    };

    const render = ({ update } = { update: true }) => {
        const value = $input.value;

        if (!value) {
            $reset.classList.remove("active");
        } else {
            $reset.classList.add("active");
        }

        if (update === false) {
            return;
        }

        tabsConfig.forEach((config, index) => {
            const { total, items, isFetched, isRecaptcha } = config;
            const $tab = tabs[index];
            const $counter = counters[index];

            if (total) {
                $counter.textContent = `(${total})`;
            } else {
                $counter.textContent = "";
            }

            $tab.textContent = "";

            if (isFetched === true && !isRecaptcha) {
                const $el = getErrorRecaptchaElement();

                $tab.append($el);
            } else if (items && items.length > 0) {
                items.forEach((item) => {
                    const $el = getItemElement(item);

                    $tab.append($el);
                });

                if (items.length < total) {
                    const $el = getMoreButtonElement();

                    $tab.append($el);
                }
            } else if (isFetched === true) {
                const $el = getNoResultElement();

                $tab.append($el);
            }
        });
    };

    const fetchData = async ({ url, params }) => {
        const stringParams = new URLSearchParams(params).toString();
        const urlWithParams = `${url}/?${stringParams}`;

        try {
            const response = await fetch(urlWithParams, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                },
            });

            const json = await response.json();

            return json;
        } catch (e) {
            throw new Error(e);
        }
    };

    const clearAll = () => {
        tabsConfig.forEach((config) => {
            config.page = 0;
            config.total = 0;
            config.items = [];
            config.isFetched = false;
        });

        render();
    };

    const handleClickMore = async (index, token) => {
        const config = tabsConfig[index];

        config.page += 1;

        grecaptcha
        .execute(window.SiteKey, { action: "Search" })
        .then(async (token) => {
            const { data } = await fetchData({
                url: config.url,
                params: {
                    query: lastQuery,
                    page: config.page,
                    lang: "en",
                    token: token,
                },
            });
            config.items = [...config.items, ...data];
            render();
        })
    };

    const handleSearch = async () => {
        const query = $input.value;

        if (!query || lastQuery === query) {
            return;
        }

        clearAll();

        lastQuery = query;

        $searchInProgress.classList.add("active");

        try {
            const promises = tabsConfig.map(({ url }) =>
                grecaptcha
                    .execute(window.SiteKey, { action: "Search" })
                    .then((token) => {
                        return fetchData({
                            url,
                            params: {
                                query,
                                page: 0,
                                lang: "en",
                                token: token,
                            },
                        });
                    })
            );

            const response = await Promise.all(promises);

            response.forEach((res, index) => {
                const { data, count, isRecaptcha } = res;
                const config = tabsConfig[index];

                config.isRecaptcha = isRecaptcha;
                config.total = count;
                config.items = [...data];
                config.isFetched = true;
            });

            render();
        } catch (e) {
            console.log(e);
        } finally {
            $searchInProgress.classList.remove("active");
        }
    };

    const handleOpen = ($el) => {
        $lastOpenElementClicked = $el;

        $body.classList.add("ns-prevent-scroll");

        $dropdown.classList.add("active");
        $dropdown.removeAttribute("inert");

        $input.focus();

        handleInertElements();
    };

    const handleClose = () => {
        $dropdown.classList.remove("active");
        $dropdown.setAttribute("inert", "true");

        handleInertElements(false);

        setTimeout(() => {
            if ($lastOpenElementClicked) {
                $lastOpenElementClicked.focus();
            }

            if (!$mobileHeader) {
                $body.classList.remove("ns-prevent-scroll");

                return;
            }

            if ($mobileHeader.classList.contains("opened")) {
                return;
            }

            $body.classList.remove("ns-prevent-scroll");
        }, 250);
    };

    const initOpen = () => {
        if (!openElements && openElements.length === 0) {
            return;
        }

        openElements.forEach(($el) => {
            $el.addEventListener("click", () => handleOpen($el));
        });
    };

    const initClose = () => {
        $close.addEventListener("click", handleClose);
    };

    const initForm = () => {
        $form.addEventListener("submit", (e) => {
            e.preventDefault();

            grecaptcha.ready(() => handleSearch());
        });
    };

    const initSearchInput = () => {
        $input.addEventListener("input", () => render({ update: false }));
        $input.addEventListener("focusout", () =>
            grecaptcha.ready(() => handleSearch())
        );
    };

    const initResetButton = () => {
        $reset.addEventListener("click", () => {
            $input.value = "";

            render({ update: false });
        });
    };

    const switchTab = (index, setFocus = false) => {
        const $button = tabButtons[index];

        tabButtons.forEach(($el) => {
            $el.classList.remove("active");
            $el.tabIndex = -1;
            $el.setAttribute("aria-selected", "false");
        });

        tabs.forEach(($el) => {
            $el.classList.remove("active");
        });

        $button.classList.add("active");
        $button.removeAttribute("tabindex");
        $button.setAttribute("aria-selected", "true");

        tabs[index].classList.add("active");

        if (setFocus === true) {
            $button.focus();
        }
    };

    const onTabKeydown = (event, index) => {
        const prevIndex = index - 1;
        const nextIndex = index + 1;

        let flag = false;

        switch (event.key) {
            case "ArrowLeft": {
                switchTab(prevIndex < 0 ? tabs.length - 1 : prevIndex, true);

                flag = true;

                break;
            }

            case "ArrowRight": {
                switchTab(nextIndex > tabs.length - 1 ? 0 : nextIndex, true);

                flag = true;

                break;
            }

            case "Home": {
                switchTab(0, true);

                flag = true;

                break;
            }

            case "End": {
                switchTab(tabs.length - 1, true);

                flag = true;

                break;
            }

            default:
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    };

    const initTabs = () => {
        tabButtons.forEach(($button, index) => {
            $button.addEventListener("click", () => switchTab(index));
            $button.addEventListener("keydown", (event) =>
                onTabKeydown(event, index)
            );
        });
    };

    const initShowMoreButtons = () => {
        document.addEventListener("click", (e) => {
            const target = e.target.closest(".js-ns-search-dropdown-more");

            if (target) {
                const $tab = target.closest(".js-ns-search-dropdown-tab");
                const index = [...tabs].indexOf($tab);

                grecaptcha.ready(() => handleClickMore(index));
            }
        });
    };

    const init = () => {
        initOpen();
        initClose();
        initSearchInput();
        initResetButton();
        initForm();
        initTabs();
        initShowMoreButtons();
    };

    const initSearchDropdown = () => {
        if (!$dropdown) {
            return;
        }

        init();
    };

    return {
        initSearchDropdown,
    };
};
